import type { Moment } from 'moment'
import moment from 'moment'
import { useCallback, useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'

import SearchFlights from '../Search/SearchFlights'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import type { FareType } from 'constants/flight'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useForm, FormProvider } from 'react-hook-form'
import FlightLookupListing from '../Search/FlightLookup/FlightLookupListing'
import {
  formOptions,
  useFieldValidation,
} from '../Common/Fields/config'
import RHFDatePickerInput from '../Common/Fields/RHFDatePickerInput'
import CreateFlight from '../Create/CreateFlight'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import { useDefaultDatePickerMonthYear } from '../Common/Hooks/useDefaultDates'
import TripContext from 'tripPlanner/contexts/TripContext'
import type { CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'
import { UppercaseInput, CustomLookupContainer, FindFlightButton } from '../Search/FlightLookup/FlightLookupStyles'

interface Props {
  tripId: string
  customOnly?: boolean
  defaultDate?: string
  prefillData?: {
    departureAirport: App.AirportLocation | undefined
    arrivalAirport: App.AirportLocation | undefined
    fareType?: FareType
    checkinDate?: Moment
    checkoutDate?: Moment
  }
  rankSpec?: CreateTripItemRankSpec
}

function SelectFlightType(props: Props) {
  const {
    tripId,
    prefillData,
    customOnly = false,
    defaultDate,
    rankSpec,
  } = props

  const { flightCarrierCodeConfig } = useFieldValidation()
  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const methods = useForm<{
    flightCode: string
    departureDate: string
  }>({
    ...formOptions,
    shouldUnregister: true,
    defaultValues: {
      departureDate: defaultDate,
      flightCode: '',
    },
  })
  const { register, getValues, handleSubmit } = methods

  const handleSearchFlightClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_search'))
    openModal(SearchFlights, { tripId, prefillData, defaultDate })
  }, [openModal, tripId, prefillData, defaultDate])

  const handleAddCustomFlightClick = useCallback(() => {
    fireInteractionEvent(
      addToTripClickEvent('modal_flights_custom_manual'),
    )
    openModal(CreateFlight, {
      tripId,
      defaultDate: getValues('departureDate'),
      initialData: { flightNumber: getValues('flightCode') },
      rankSpec,
    })
  }, [getValues, openModal, tripId, rankSpec])

  const onSubmit = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_custom_lookup'))
    openModal(FlightLookupListing, {
      tripId,
      flightCode: getValues('flightCode').toUpperCase(),
      departureDate: moment(getValues('departureDate')),
    })
  }, [openModal, getValues, tripId])

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const modalTitle = intl.formatMessage({
    id: '_lQDWxTptOF5qZQjhauq',
    defaultMessage: 'Flight',
    description: 'Modal title for flight type selection in trip planner',
  })
  const flightNumberLabel = intl.formatMessage({
    id: 'TTf-ltnlpGka8U0Ptr4z',
    defaultMessage: 'Flight number',
    description: 'Form label for flight number input in trip planner flight lookup',
  })
  const flightNumberPlaceholder = intl.formatMessage({
    id: '1tmKb0Ds9Ch9sHMvUlL1',
    defaultMessage: 'e.g. QF1',
    description: 'Placeholder example for flight number input in trip planner',
  })
  const departureDayLabel = intl.formatMessage({
    id: 'bHyuZOrydqrT7SUJRYoD',
    defaultMessage: 'Departure day',
    description: 'Form label for departure date picker in trip planner flight lookup',
  })
  const findButtonLabel = intl.formatMessage({
    id: 'MP_Ancrj-UauJTQuoUew',
    defaultMessage: 'Find',
    description: 'Button text to look up a flight by number and date in trip planner',
  })
  const orDividerLabel = intl.formatMessage({
    id: 'RSfQOXtXeQr-uS5yLTiG',
    defaultMessage: 'or',
    description: 'Divider text between flight lookup and manual entry options in trip planner',
  })
  const searchLuxuryEscapesLabel = intl.formatMessage({
    id: 'FwZwqCAPupyTMVR6T6zy',
    defaultMessage: 'Search Luxury Escapes',
    description: 'Button text to search Luxury Escapes flight offers in trip planner',
  })
  const manuallyAddDetailsLabel = intl.formatMessage({
    id: 'BFuBoc2iiwLdZdEBb3AT',
    defaultMessage: 'Manually add details',
    description: 'Button text to manually enter flight details in trip planner',
  })

  const { startDate: tripStartDate } = useContext(TripContext) ?? {}

  const today = moment()
  const [minDate, maxDate] = useMemo(() => {
    const maxDate = tripStartDate ? tripStartDate.clone().add(12, 'months').toDate() : today.clone().add(12, 'months').toDate()

    return [
      today.clone().subtract(1, 'days').toDate(),
      maxDate,
    ]
    // Disable exhaustive-deps because we only want to recalculate min/max date
    // if the actual date has changed.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [today.format(ISO_DATE_FORMAT), tripStartDate])

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={16}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <CustomLookupContainer>
                  <UppercaseInput
                    {...register('flightCode', {
                      ...flightCarrierCodeConfig,
                      setValueAs: (val: string) => val.toUpperCase(),
                    })}
                    required
                    label={flightNumberLabel}
                    placeholder={flightNumberPlaceholder}
                  />
                  <RHFDatePickerInput
                    name="departureDate"
                    label={departureDayLabel}
                    min={minDate}
                    max={maxDate}
                    defaultMonth={defaultMonth}
                    defaultYear={defaultYear}
                    required
                  />
                  <VerticalSpacer
                    gap={8}
                  >
                    <br />
                    <FindFlightButton
                      kind="primary"
                      size="large"
                      type="submit"
                      fit="full-width"
                    >
                      {findButtonLabel}
                    </FindFlightButton>
                  </VerticalSpacer>

                </CustomLookupContainer>
              </form>
            </FormProvider>

            <Group direction="horizontal" gap={16} verticalAlign="center">
              <Divider kind="primary" />
              <BodyText variant="medium" weight="bold" colour="neutral-three">{orDividerLabel}</BodyText>
              <Divider kind="primary" />
            </Group>

            <Group
              direction="vertical"
              tabletDirection="horizontal"
              gap={8}
              tabletGap={16}
              horizontalAlign="stretch"
              verticalAlign="stretch"
            >
              {!customOnly &&
                <FindFlightButton
                  kind="secondary"
                  size="large"
                  onClick={handleSearchFlightClick}
                >
                  {searchLuxuryEscapesLabel}
                </FindFlightButton>}
              <FindFlightButton
                kind="secondary"
                size="large"
                onClick={handleAddCustomFlightClick}
              >
                {manuallyAddDetailsLabel}
              </FindFlightButton>
            </Group>
          </Group>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SelectFlightType
